import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClearancesTab } from "./components/ClearancesTab";
import { DriverTab } from "./components/DriverTab";
import { HosSettingsTab } from "./components/HosSettingsTab";
import { SystemCommunicationsTab } from "./components/SystemCommunicationsTab";
import { UiSettingsTab } from "./components/UiSettingsTab";
import { UserList } from "./components/UserList";
import { UserTab } from "./components/UserTab";
import { useUserMutations } from "./hooks/useUserMutations";
import {
  UserStatusFilter,
  UserTab as UserListTab,
  useUsersPageData,
} from "./hooks/useUsersPageData";
import {
  SetUserField,
  UserForm,
  canManageUser,
  emptyUserForm,
  isArchived,
  isSameUser,
  userToForm,
} from "./usersPageHelpers";
import { GroupsTruncatedAlert } from "@/components/GroupsTruncatedAlert";
import { LoadingRow } from "@/components/LoadingRow";
import { COMPANY_GROUP_ID } from "@/constants/geotabGroupIds";
import { RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

type DetailTab =
  | "user"
  | "driver"
  | "uiSettings"
  | "hosSettings"
  | "systemCommunications"
  | "clearances";

export default function UsersPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<UserListTab>("users");
  const [detailTab, setDetailTab] = useState<DetailTab>("user");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("active");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<UserForm>(() =>
    emptyUserForm({ isDriver: false, creating: false }),
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const selectionRef = useRef({ creating: isCreating, selectedUserId });
  selectionRef.current = { creating: isCreating, selectedUserId };
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const {
    me,
    loadError,
    isUserListTruncated,
    groupsError,
    securityBranchError,
    usersError,
    isLoading,
    isRefreshingUsers,
    sortedUsers,
    debouncedSearch,
    selectedUser,
    selectedUserById,
    selectedUserSecurityGroups,
    groupById,
    securityGroupsSorted,
    companyGroupOptions,
    assignableClearanceIds,
  } = useUsersPageData({ search, tab, statusFilter, selectedUserId, isCreating });
  const groupByIdRef = useRef(groupById);
  groupByIdRef.current = groupById;

  const setField: SetUserField = (key, value) => setForm(current => ({ ...current, [key]: value }));

  useEffect(() => {
    const isDriver = tabRef.current === "drivers";
    if (isCreating) {
      const companyDefault = groupByIdRef.current.get(COMPANY_GROUP_ID);
      setForm(emptyUserForm({ isDriver, creating: true, companyDefault }));
    } else if (selectedUser) {
      setForm(userToForm(selectedUser, groupByIdRef.current));
    } else {
      setForm(emptyUserForm({ isDriver, creating: false }));
    }
  }, [isCreating, selectedUser]);

  useEffect(() => {
    setDetailTab("user");
  }, [selectedUserId, isCreating]);

  const clearMessages = useCallback(() => {
    setFormError(null);
    setFormSuccess(null);
  }, []);

  const selectUser = useCallback(
    (userId: string) => {
      clearMessages();
      setIsCreating(false);
      setSelectedUserId(userId);
    },
    [clearMessages],
  );

  const { saveMutation, archiveMutation, removeMutation } = useUserMutations({
    form,
    isCreating,
    selectedUser,
    me,
    assignableClearanceIds,
    selectionRef,
    setIsCreating,
    setSelectedUserId,
    setFormError,
    setFormSuccess,
  });

  const isSelf = isSameUser(selectedUser, me);
  const canManage = isCreating
    ? assignableClearanceIds.size > 0
    : !!selectedUserById && canManageUser(selectedUserById, assignableClearanceIds);

  const currentUserArchived = selectedUser ? isArchived(selectedUser) : false;
  const panelProps = { form, setField, isMobile };

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      spacing={2}
      sx={{ p: 2, height: "100%", boxSizing: "border-box", minWidth: 0 }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: isMobile ? "100%" : "25%",
          minWidth: isMobile ? 0 : 320,
          boxSizing: "border-box",
          p: 1.5,
          overflow: "auto",
        }}
      >
        <Stack spacing={1.25}>
          <Tabs value={tab} onChange={(_e, value) => setTab(value)}>
            <Tab value="users" label={t("users.tabs.users")} />
            <Tab value="drivers" label={t("users.tabs.drivers")} />
          </Tabs>

          <TextField
            size="small"
            value={search}
            onChange={event => setSearch(event.target.value)}
            label={t("users.search")}
          />

          <TextField
            size="small"
            select
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value as UserStatusFilter)}
            label={t("users.status")}
          >
            <MenuItem value="active">{t("users.statusActive")}</MenuItem>
            <MenuItem value="archived">{t("users.statusArchived")}</MenuItem>
            <MenuItem value="all">{t("users.statusAll")}</MenuItem>
          </TextField>

          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              clearMessages();
              setSelectedUserId(null);
              setIsCreating(true);
            }}
          >
            {t("users.createUser")}
          </Button>

          <Divider />

          {usersError ? <Alert severity="error">{t("users.listLoadError")}</Alert> : null}
          {isRefreshingUsers ? <LinearProgress /> : null}
          {isLoading ? (
            <LoadingRow label={t("users.loading")} />
          ) : (
            <UserList
              key={`${tab}|${statusFilter}|${debouncedSearch}`}
              users={sortedUsers}
              selectedUserId={isCreating ? null : selectedUserId}
              onSelect={selectUser}
            />
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ width: isMobile ? "100%" : "75%", minWidth: 0, boxSizing: "border-box", p: 2, overflow: "auto" }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1}>
            <Typography variant="h6" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              {isCreating
                ? t("users.newUser")
                : selectedUser
                  ? getUserDisplayName(selectedUser)
                  : t("users.noSelection")}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={() => saveMutation.mutate()}
                disabled={!canManage || groupsError || saveMutation.isPending}
              >
                {t("users.save")}
              </Button>

              {selectedUser ? (
                <Button
                  startIcon={currentUserArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                  variant="outlined"
                  onClick={() => archiveMutation.mutate(!currentUserArchived)}
                  disabled={isSelf || !canManage || archiveMutation.isPending}
                >
                  {currentUserArchived ? t("users.unarchive") : t("users.archive")}
                </Button>
              ) : null}

              {selectedUser ? (
                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSelf || !canManage || removeMutation.isPending}
                >
                  {t("users.delete")}
                </Button>
              ) : null}

              <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                <DialogTitle>{t("users.confirmDeleteTitle")}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {t("users.confirmDeleteText", {
                      name: selectedUser ? getUserDisplayName(selectedUser) : "",
                    })}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmDeleteOpen(false)}>{t("users.cancel")}</Button>
                  <Button
                    color="error"
                    onClick={() => {
                      removeMutation.mutate();
                      setConfirmDeleteOpen(false);
                    }}
                  >
                    {t("users.delete")}
                  </Button>
                </DialogActions>
              </Dialog>
            </Stack>
          </Stack>

          {formSuccess ? <Alert severity="success">{formSuccess}</Alert> : null}
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          {selectedUser && !canManage ? (
            <Alert severity="info">{t("users.cannotManageUser")}</Alert>
          ) : null}
          {isSelf ? <Alert severity="info">{t("users.selfProtected")}</Alert> : null}
          {loadError ? <Alert severity="warning">{t("users.loadError")}</Alert> : null}
          {isUserListTruncated ? (
            <Alert severity="warning">{t("users.listTruncated", { count: RESULTS_LIMIT_USER })}</Alert>
          ) : null}
          <GroupsTruncatedAlert />

          <Divider />

          <Tabs
            value={detailTab}
            onChange={(_event, value) => setDetailTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab value="user" label={t("users.detailTabs.user")} />
            <Tab value="driver" label={t("users.detailTabs.driver")} />
            <Tab value="uiSettings" label={t("users.detailTabs.uiSettings")} />
            <Tab value="hosSettings" label={t("users.detailTabs.hosSettings")} />
            <Tab value="systemCommunications" label={t("users.detailTabs.systemCommunications")} />
            <Tab value="clearances" label={t("users.detailTabs.clearances")} />
          </Tabs>

          {detailTab === "user" ? (
            <UserTab {...panelProps} lastAccessDate={selectedUser?.lastAccessDate} />
          ) : null}
          {detailTab === "driver" ? <DriverTab {...panelProps} /> : null}
          {detailTab === "uiSettings" ? <UiSettingsTab {...panelProps} /> : null}
          {detailTab === "hosSettings" ? <HosSettingsTab {...panelProps} /> : null}
          {detailTab === "systemCommunications" ? (
            <SystemCommunicationsTab {...panelProps} isCreating={isCreating} />
          ) : null}
          {detailTab === "clearances" ? (
            <ClearancesTab
              form={form}
              setField={setField}
              selectedUserSecurityGroups={selectedUserSecurityGroups}
              securityGroupsSorted={securityGroupsSorted}
              assignableClearanceIds={assignableClearanceIds}
              companyGroupOptions={companyGroupOptions}
              roleDisabled={isSelf || !canManage || securityBranchError}
              onRoleChange={clearMessages}
            />
          ) : null}
        </Stack>
      </Paper>
    </Stack>
  );
}
